import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError,
    combineLatest,
    concatMap,
    filter,
    first,
    from,
    map,
    of,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs';

import { PrivaLocalizationService } from '@priva/localization';
import { PrivaNotificationsService } from '@priva/notifications';

import { ContainerMeasurement } from 'app/features/manual-input-result-registration/models/container-measurement';
import { getApplicationName } from 'app/features/settings';
import {
    selectAppMode,
    selectAppModeIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import { notificationSuccessTimeout } from 'app/shared/constants';
import {
    containerMeasurementDataStorageKey,
    DeviceInfoStorageService,
    StorageService,
    TerminalClient,
} from 'app/shared/services';

import { selectActivityByNumber } from '../activities/activity.selectors';
import { resetApiConfig } from '../api-config/api-config.actions';
import { selectApiConfigState } from '../api-config/api-config.selectors';
import { selectContainerByNumber } from '../containers/container.selectors';
import { selectEmployeeByNumber } from '../employee/employee.selectors';
import { selectProductBoxListBuNumbers } from '../product-boxes/product-box.selectors';
import {
    selectUserSessionCredentials,
    selectUserSessionEmployeeNumber,
} from '../user-session/user-session.selectors';
import {
    addContainerMeasurementRegistration,
    addContainerMeasurementsToCache,
    initializeContainerMeasurementStore,
    resetContainerMeasurementState,
    sendCachedContainerMeasurements,
    sendCachedContainerMeasurementsFailure,
    sendCachedContainerMeasurementsSuccess,
    showingToastAfterContainerMeasurement,
    showingToastAfterContainerMeasurementSuccess,
} from './container-measurements.actions';
import {
    selectCachedContainerMeasurementsAsArray,
    selectContainerMeasurementsState,
} from './container-measurements.selectors';
import { getContainerMeasurementMessage } from './container-measurements.utils';

@Injectable()
export class ContainerMeasurementsEffects {
    privaLocalizationService = inject(PrivaLocalizationService);

    loadFromStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeContainerMeasurementStore),
            map(
                () =>
                    this.storageService
                        .get<Partial<ContainerMeasurement>[]>(containerMeasurementDataStorageKey)
                        ?.map(
                            (p): ContainerMeasurement => ({
                                activityNumber: p.activityNumber ?? 0,
                                amountResult: p.amountResult || undefined,
                                containerNumber: p.containerNumber ?? 0,
                                employeeNumber: p.employeeNumber ?? 0,
                                productBoxRegistrations: p.productBoxRegistrations || [],
                                registrationTimestamp: p.registrationTimestamp ?? 0,
                            }),
                        ) ?? [],
            ),
            filter((containerMeasurements) => !!containerMeasurements.length),
            map((containerMeasurements) => addContainerMeasurementsToCache({ containerMeasurements })),
        ),
    );

    addContainerMeasurementForPackingActivity$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addContainerMeasurementRegistration),
            withLatestFrom(
                this.store.select(selectAppMode),
                this.store.select(selectUserSessionEmployeeNumber),
            ),
            map(
                ([
                    {
                        activityNumber,
                        amountResult,
                        containers,
                        employeeNumberMultiUserMode,
                        productBoxRegistrations,
                        location,
                    },
                    appMode,
                    userSessionEmployeeNumber,
                ]): ContainerMeasurement | undefined => {
                    const employeeNumberForRegistration =
                        appMode === 'multi-user' ? employeeNumberMultiUserMode : userSessionEmployeeNumber;

                    if (!employeeNumberForRegistration) {
                        return undefined;
                    }

                    return {
                        activityNumber,
                        amountResult: amountResult,
                        containerNumber: containers.at(0)!.number,
                        employeeNumber: employeeNumberForRegistration,
                        productBoxRegistrations,
                        registrationTimestamp: Date.now(),
                    };
                },
            ),
            filter(
                (containerMeasurement): containerMeasurement is ContainerMeasurement =>
                    !!containerMeasurement,
            ),
            switchMap((containerMeasurement) => {
                return [
                    addContainerMeasurementsToCache({ containerMeasurements: [containerMeasurement] }),
                    showingToastAfterContainerMeasurement({ containerMeasurements: [containerMeasurement] }),
                ];
            }),
        );
    });

    sendContainerMeasurementsOnCacheChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addContainerMeasurementsToCache),
            map(() => sendCachedContainerMeasurements()),
        );
    });

    sendCachedContainerMeasurements$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendCachedContainerMeasurements),
            concatMap(() => {
                return combineLatest([
                    this.store.select(selectContainerMeasurementsState),
                    this.store.select(selectUserSessionCredentials),
                    this.store.select(selectApiConfigState),
                    this.store.pipe(selectAppModeIfDefined),
                ]).pipe(
                    take(1),
                    filter(([, , { identifier }]) => !!identifier),
                    switchMap(
                        ([
                            { cachedContainerMeasurements, previousUserSessionCredentials },
                            userSessionCredentials,
                            { identifier, apiVersion },
                            appMode,
                        ]) => {
                            // When cached registrations exceed a certain limit, it may not be sent to the server to large amount limit
                            // To prevent this from happening, registrations will be sent in batches of 200 at one time
                            const sendCachedContainerMeasurements = Object.fromEntries(
                                Object.entries(cachedContainerMeasurements).slice(0, 200),
                            );
                            const containerMeasurements = Object.values(sendCachedContainerMeasurements);

                            const applicationName = getApplicationName(appMode);
                            const deviceId = this.deviceInfoStorageService.getOrAddDeviceId();

                            const credentials = userSessionCredentials ?? previousUserSessionCredentials;

                            if (!credentials) {
                                return of(
                                    sendCachedContainerMeasurementsFailure({
                                        errorMessage:
                                            this.privaLocalizationService.instant('ERROR.TITLE.UNAUTHORIZED'),
                                    }),
                                );
                            }
                            return this.client
                                .sendRegistrations({
                                    registrations: [],
                                    credentials,
                                    apiVersion,
                                    identifier,
                                    deviceId,
                                    applicationName,
                                    containerMeasurements,
                                })
                                .pipe(
                                    switchMap((result) => {
                                        if (!result.success)
                                            return [
                                                sendCachedContainerMeasurementsFailure({
                                                    errorMessage: result.error,
                                                }),
                                            ];
                                        return result.feedback
                                            ? [
                                                  sendCachedContainerMeasurementsSuccess({
                                                      sentContainerMeasurements:
                                                          sendCachedContainerMeasurements,
                                                  }),
                                                  showingToastAfterContainerMeasurementSuccess({
                                                      feedback: result.feedback,
                                                  }),
                                              ]
                                            : [
                                                  sendCachedContainerMeasurementsSuccess({
                                                      sentContainerMeasurements:
                                                          sendCachedContainerMeasurements,
                                                  }),
                                              ];
                                    }),
                                    catchError((e) =>
                                        of(
                                            sendCachedContainerMeasurementsFailure({
                                                errorMessage: e.message,
                                            }),
                                        ),
                                    ),
                                );
                        },
                    ),
                );
            }),
        ),
    );

    storeContainerMeasurements$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(addContainerMeasurementsToCache, sendCachedContainerMeasurementsSuccess),
                withLatestFrom(this.store.select(selectCachedContainerMeasurementsAsArray)),
                map(([, containerMeasurements]) => {
                    this.storageService.store(containerMeasurementDataStorageKey, containerMeasurements);
                    return true;
                }),
            );
        },
        { dispatch: false },
    );

    showToastAfterContainerMeasurement = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(showingToastAfterContainerMeasurement),
                switchMap(({ containerMeasurements }) => {
                    return from(containerMeasurements);
                }),
                switchMap((containerMeasurement) => {
                    return combineLatest([
                        this.store.pipe(selectEmployeeByNumber(containerMeasurement.employeeNumber)),
                        this.store.pipe(selectActivityByNumber(containerMeasurement.activityNumber)),
                        this.store.select(
                            selectProductBoxListBuNumbers(
                                containerMeasurement.productBoxRegistrations.map(
                                    ({ productBoxNumber }) => productBoxNumber,
                                ),
                            ),
                        ),
                        this.store.select(selectContainerByNumber(containerMeasurement.containerNumber)),
                    ]).pipe(
                        first(),
                        map(([employee, activity, productBox, container]) => ({
                            containerMeasurement,
                            employee,
                            activity,
                            productBox,
                            container,
                        })),
                    );
                }),
                map(({ containerMeasurement, employee, activity, productBox, container }) => {
                    const toasterMessage = getContainerMeasurementMessage(
                        containerMeasurement,
                        activity,
                        productBox,
                        container,
                    );

                    this.notificationsService.toaster.success(
                        {
                            emphasized: `${employee?.name}`,
                            message: toasterMessage,
                            emphasisBlock: true,
                            params: { activityName: activity?.name },
                        },
                        { behavior: 'transient', timeout: notificationSuccessTimeout },
                    );
                }),
            );
        },
        { dispatch: false },
    );

    resetContainerMeasurementStateAfterResetApiConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(resetApiConfig),
            map(() => resetContainerMeasurementState()),
        ),
    );

    clearContainerMeasurementsInStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(resetContainerMeasurementState),
                map(() => {
                    this.storageService.clear(containerMeasurementDataStorageKey);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly client: TerminalClient,
        private readonly deviceInfoStorageService: DeviceInfoStorageService,
        private readonly storageService: StorageService,
        private readonly notificationsService: PrivaNotificationsService,
    ) {}
}
